import * as Immutable from 'immutable';
import * as React from 'react';
import StageLayer from '../../Layer/_components/StageLayer';
import StageLayerContainer from '../../Layer/_containers/StageLayerContainer';
import LayerStore from '../../Layer/_stores/LayerStore';
import { IModuleStore } from '../../Module/_stores/ModuleStore';
import CanvasWrapper from '../../Webgl/CanvasWrapper';
import Dimensions from '../../Utils/Dimensions';
import { DOCUMENT_QUALITY_MIN, DOCUMENT_QUALITY_MAX, DOCUMENT_QUALITY_CHECK, DOCUMENT_MAX_RESIZE_PER_SECOND, DOCUMENT_QUALITY_THRESHOLD_DOWN, DOCUMENT_QUALITY_THRESHOLD_UP, DOCUMENT_MIN_REPEAT, DOCUMENT_MAX_REPEAT } from '../../_config/Consts';
import RateLimiter from '@af-common/Common/Utils/RateLimiter';
import { IS_SERVER } from '@af-app/config';
import AwareComponent from '@af-common/Common/_components/AwareComponent';
import Webgl from "../../Webgl/Webgl";
import SimpleDrawShader from "../../Webgl/Shader/SimpleDrawShader";
import AnimFrameHandler from "../../Utils/AnimFrameHandler";

export type Props = {
	layers: Immutable.OrderedMap<string, LayerStore>,
	documentWidth: number,
	documentHeight: number,
	draw: Function,
	setDPI?: Function,
	setQuality?: Function,
	centerDocument?: Function,
	increaseQuality?: Function,
	decreaseQuality?: Function,
	documentQuality?: number,
	documentDPI?: number,
}

export default class WorkCanvas extends AwareComponent<Props> {
	private rl:RateLimiter;
	private animFrameHandler:AnimFrameHandler;
	private checkQuality:boolean;
	private frames:number[];
	private fps:number;

	private quality = () => {
		let animFrame = IS_SERVER ? (func:Function) => func() : (func:FrameRequestCallback) => window.requestAnimationFrame(func);
		animFrame(() => {
			if(!this.checkQuality) return;
			let now = performance.now();
			while (this.frames.length > 0 && this.frames[0] <= now - DOCUMENT_QUALITY_CHECK) {
				this.frames.shift();
			}
			this.frames.push(now);
			this.fps = this.frames.length * (1000 / DOCUMENT_QUALITY_CHECK);

			this.rl.throttle(() => {
				//Calc max quality based on max total pixels
				let maxRenderDimensions = Dimensions.getRenderDimensions(this.props.documentWidth, this.props.documentHeight);
				let maxQuality:number = Math.min(Math.min(maxRenderDimensions.width / this.props.documentWidth, maxRenderDimensions.height / this.props.documentHeight) * 100, DOCUMENT_QUALITY_MAX);
				
				if(this.props.documentQuality < maxQuality && this.fps >= DOCUMENT_QUALITY_THRESHOLD_UP){
					this.props.increaseQuality(maxQuality);
				} else if (this.props.documentQuality > DOCUMENT_QUALITY_MIN && this.fps < DOCUMENT_QUALITY_THRESHOLD_DOWN) {
					this.props.decreaseQuality();
				}
			});

			this.quality();
		});
	}

	constructor(props:Props){
		super(props);
		this.rl = new RateLimiter(1000 / DOCUMENT_MAX_RESIZE_PER_SECOND);
		this.animFrameHandler = new AnimFrameHandler;
		this.frames = [];
		this.fps = 0;
	}

	componentDidUpdate(prevProps:Props){
		if(this.propChanged(prevProps, 'documentQuality')){
			let webgl = Webgl.getInstance("layers", this.props.documentWidth * (this.props.documentQuality / 100), this.props.documentHeight * (this.props.documentQuality / 100));
			webgl.resizeInstance("layers", this.props.documentWidth * (this.props.documentQuality / 100), this.props.documentHeight * (this.props.documentQuality / 100));
		}

		if(this.props.documentWidth && this.props.documentHeight) {
			if(this.propChanged(prevProps, 'documentWidth', 'documentHeight')){
				//Reset quality
				this.props.setQuality(1);

				//Reset dpi to a nicer value
				let newDpi = Dimensions.getMinDPI(this.props.documentWidth, this.props.documentHeight) + (Dimensions.getMaxDPI(this.props.documentWidth, this.props.documentHeight) - Dimensions.getMinDPI(this.props.documentWidth, this.props.documentHeight)) / 4;
				this.props.setDPI(newDpi);

				//Center document
				this.props.centerDocument();
			}
		}
	}

	componentDidMount(){
		this.checkQuality = true;
		this.quality();
	}

	componentWillUnmount(){
		this.rl.stop();
		this.checkQuality = false;

		this.animFrameHandler.flush();
	}

	private drawLayer = () => {
		let webgl = Webgl.getInstance("layers", this.props.documentWidth * (this.props.documentQuality / 100), this.props.documentHeight * (this.props.documentQuality / 100));
		if (!webgl.programs['simple_draw']) {
			webgl.createProgram('simple_draw', new SimpleDrawShader());
		}
		webgl.useProgram('simple_draw');

		webgl.currentProgram.bindTexture("u_background", webgl.fbos[webgl.getPreviousFbo("layers")].texture);
		webgl.render(webgl.getPreviousFbo("worker"), false);

		webgl.clearInstance("worker");
	}

	private getLastVisibleLayer = () => {
		let layerId:string = null;
		this.props.layers && this.props.layers.entrySeq().forEach(([id, layer], key) => {
			if(layer.get('visible')) layerId = id;
		});
		return layerId;
	}

	private clearLayers = () => {
		let webgl = Webgl.getInstance("layers", this.props.documentWidth * (this.props.documentQuality / 100), this.props.documentHeight * (this.props.documentQuality / 100));
		if(!webgl) return;
		webgl.clearInstance("layers");
	}

	render() {
		let lastVisible = this.getLastVisibleLayer();

		this.animFrameHandler.flush();
		this.animFrameHandler.addProcess(this.clearLayers.bind(this));

		if(!this.props.layers.count() || !lastVisible){
			this.animFrameHandler.addProcess(this.props.draw);
			this.animFrameHandler.execute();
			return <></>
		}

		let output:React.ReactElement[] = [];
		this.props.layers && this.props.layers.entrySeq().forEach(([id, layer], key) => {
			layer.get('visible') && output.push(<StageLayerContainer animFrameHandler={this.animFrameHandler} key={key+Date.now()} id={id} drawLayer={this.drawLayer} draw={this.props.draw} isLastVisible={id == lastVisible} />);
		});

		return output;
	}
}